// Typed fetch client for the app-api Worker. credentials: 'include' on every
// call so __Host-session + csrf cookies flow across the
// seo-monitor.pages.dev <-> seo-monitor-api.workers.dev origins (CORS is
// configured to allow this on the API side).
//
// VITE_API_BASE is set via .env.local for dev (defaults to localhost:8787).

const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:8787';

export type Ok<T> = { ok: true; data: T };
export type Err = {
  ok: false;
  error: { code: string; message: string; details?: Record<string, unknown> };
};
export type ApiResponse<T> = Ok<T> | Err;

// CSRF token comes from the `csrf` cookie set at session creation (intentionally
// not HttpOnly so JS can read it for the X-CSRF-Token header).
function readCsrf(): string | null {
  const m = document.cookie.match(/(?:^|; )csrf=([^;]+)/);
  return m?.[1] ?? null;
}

export async function apiGet<T>(path: string): Promise<ApiResponse<T>> {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    return (await res.json()) as ApiResponse<T>;
  } catch {
    // Network error, CORS rejection, JSON parse failure → treat as unreachable.
    // Auth flows specifically depend on this returning ok:false so the SPA can
    // redirect to /login rather than crashing on a thrown fetch.
    return { ok: false, error: { code: 'NETWORK', message: 'API unreachable' } };
  }
}

export async function apiPost<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
  const csrf = readCsrf();
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return (await res.json()) as ApiResponse<T>;
  } catch {
    return { ok: false, error: { code: 'NETWORK', message: 'API unreachable' } };
  }
}

// --- Typed endpoint wrappers (mirror the apps/api response shapes) ---

export type Me = {
  user: { id: number; email: string; role: 'admin' | 'viewer' };
  session: { expires_at: number };
};
export const getMe = () => apiGet<Me>('/auth/me');
export const logout = () => apiPost<{ logged_out: true }>('/auth/logout');

// Constructs the URL the SPA navigates to (full-page redirect, NOT an XHR)
// to start the Google OAuth flow on the API worker. The API does the OAuth
// dance and redirects back to the SPA after creating a session.
//
// `redirectTo` is an optional same-origin path to land on after successful
// sign-in (e.g. the original destination the user tried to access before
// being bounced to /login). The API echoes this through Google's state and
// uses it in the final redirect.
export function googleSignInUrl(redirectTo?: string): string {
  const url = new URL(`${API_BASE}/auth/google`);
  if (redirectTo) url.searchParams.set('redirect', redirectTo);
  return url.toString();
}
